import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getBaseURL } from "../pages/constants";

export const BASE_URL = getBaseURL();
export const applicationApi = createApi({
  reducerPath: "applicationApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  mode: "cors",
  tagTypes: ["Users", "Roles", "Permissions", "AuditLogs", "XXX"],
  endpoints: (builder) => ({
    // user related APIs
    createUser: builder.mutation({
      query: (payload) => {
        return {
          url: "users/",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: JSON.stringify(payload),
        };
      },
      invalidatesTags: ["Users"],
    }),
    loginUser: builder.mutation({
      query: (payload) => {
        return {
          url: "users/login",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        };
      },
      invalidatesTags: [],
    }),
    getAllUsers: builder.query({
      query: (id) => {
        return {
          url: `users/?page=${id.page}&limit=${id.limit}&search=${id.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Users"],
    }),
    modifyUser: builder.mutation({
      query: (params) => {
        return {
          url: `users/${params.userId}`,
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.values),
        };
      },
      invalidatesTags: ["Users"],
    }),
    getRolesByUserId: builder.query({
      query: (id) => {
        return {
          url: `users/${id.userId}/roles?page=${id.page}&limit=${id.limit}&search=${id.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Roles"],
    }),
    removeRole: builder.mutation({
      query: (params) => {
        return {
          url: `users/${params.userId}/roles/${params.roleId}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Roles"],
    }),
    addRole: builder.mutation({
      query: (params) => {
        return {
          url: `users/${params.userId}/roles`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ roleIds: params.roleIds }),
        };
      },
      invalidatesTags: ["Roles"],
    }),
    changePwd: builder.mutation({
      query: (params) => {
        return {
          url: `users/changePwd`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            oldPassword: params.oldPassword,
            newPassword: params.newPassword,
          }),
        };
      },
      invalidatesTags: ["Users"],
    }),
    resetPwd: builder.mutation({
      query: (params) => {
        return {
          url: `users/resetPwd/${params.userId}`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ newPasswd: params.newPasswd }),
        };
      },
      invalidatesTags: ["Users"],
    }),
    deleteUser: builder.mutation({
      query: (params) => {
        return {
          url: `users/${params.userId}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Users"],
    }),

    // roles
    getRoles: builder.query({
      query: (id) => {
        return {
          url: `roles/?page=${id.page}&limit=${id.limit}&search=${id.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Roles"],
    }),
    createRole: builder.mutation({
      query: (params) => {
        return {
          url: "roles/",
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name: params.name }),
        };
      },
      invalidatesTags: ["Roles"],
    }),
    updateRole: builder.mutation({
      query: (params) => {
        return {
          url: `roles/${params.roleId}`,
          method: "PUT",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name: params.name }),
        };
      },
      invalidatesTags: ["Roles"],
    }),
    deleteRole: builder.mutation({
      query: (params) => {
        return {
          url: `roles/${params.roleId}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Roles"],
    }),
    addPermissionToRole: builder.mutation({
      query: (params) => {
        return {
          url: `roles/${params.roleId}/permissions`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ permissionIds: params.permissionIds }),
        };
      },
      invalidatesTags: ["Roles"],
    }),
    removePermissionFromRole: builder.mutation({
      query: (params) => {
        return {
          url: `roles/${params.roleId}/permissions/${params.permissionId}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Roles"],
    }),
    getPermissionsOfRole: builder.query({
      query: (id) => {
        return {
          url: `roles/${id.roleId}/permissions?page=${id.page}&limit=${id.limit}&search=${id.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Permissions"],
    }),

    // permissions
    getAllPermissions: builder.query({
      query: (id) => {
        return {
          url: `permissions/?page=${id.page}&limit=${id.limit}&search=${id.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Permissions"],
    }),
    getAvailablePermissions: builder.query({
      query: (id) => {
        return {
          url: `roles/${id.roleId}/permissionsAvailable?page=${id.page}&limit=${id.limit}&search=${id.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Permissions"],
    }),

    // audit
    getAuditLogs: builder.query({
      query: (id) => {
        return {
          url: `/audit/?page=${id.page}&limit=${id.limit}&search=${id.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["AuditLogs"],
    }),
    getAuditLogsById: builder.query({
      query: (id) => {
        return {
          url: `audit/${id.auditId}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["AuditLogs"],
    }),

    // bandobast
    getAllBandobast: builder.query({
      query: (id) => {
        return {
          url: `bandobast/?page=${id.page}&limit=${id.limit}&search=${id.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Bandobast"],
    }),
    getBandobastDetails: builder.query({
      query: (id) => {
        return {
          url: `bandobast/${id}/details`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: [
        "Bandobast",
        "PersonnelAllocations",
        "Locations",
        "PersonnelAssessments",
      ],
    }),
    getBandobastSummary: builder.query({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/summary`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: [
        "Bandobast",
        "PersonnelAllocations",
        "Locations",
        "PersonnelAssessments",
      ],
    }),
    newBandobast: builder.mutation({
      query: (params) => {
        return {
          url: "bandobast/",
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        };
      },
      invalidatesTags: ["Bandobast"],
    }),
    editBandobast: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.id}`,
          method: "PUT",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Bandobast"],
    }),
    deleteBandobast: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.id}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Bandobast"],
    }),
    uploadLeftLogo: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/uploadLeftLogo`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["Bandobast"],
    }),
    uploadRightLogo: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/uploadRightLogo`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["Bandobast"],
    }),
    uploadSignature: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/uploadSignature`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["Bandobast"],
    }),

    // resource types
    getResourceTypes: builder.query({
      query: (id) => {
        return {
          url: "resourceTypes/",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["ResourceTypes"],
    }),
    newResourceType: builder.mutation({
      query: (params) => {
        return {
          url: "resourceTypes/",
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        };
      },
      invalidatesTags: ["ResourceTypes"],
    }),
    editResourceType: builder.mutation({
      query: (params) => {
        return {
          url: `resourceTypes/${params.id}`,
          method: "PUT",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["ResourceTypes"],
    }),
    deleteResourceType: builder.mutation({
      query: (params) => {
        return {
          url: `resourceTypes/${params.id}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["ResourceTypes"],
    }),
    uploadResourceType: builder.mutation({
      query: (params) => {
        return {
          url: `resourceTypes/upload`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["ResourceTypes"],
    }),

    // resources
    getResources: builder.query({
      query: (id) => {
        return {
          url: "resources/",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Resources"],
    }),
    newResource: builder.mutation({
      query: (params) => {
        return {
          url: "resources/",
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        };
      },
      invalidatesTags: ["Resources"],
    }),
    editResource: builder.mutation({
      query: (params) => {
        return {
          url: `resources/${params.id}`,
          method: "PUT",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Resources"],
    }),
    deleteResource: builder.mutation({
      query: (params) => {
        return {
          url: `resources/${params.id}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Resources"],
    }),
    uploadResource: builder.mutation({
      query: (params) => {
        return {
          url: `resources/upload`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["Resources"],
    }),

    // resource template
    getResourceTemplates: builder.query({
      query: (id) => {
        return {
          url: "resourceAssessmentTemplates/",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["ResourceTemplates"],
    }),
    newResourceTemplate: builder.mutation({
      query: (params) => {
        return {
          url: "resourceAssessmentTemplates/",
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        };
      },
      invalidatesTags: ["ResourceTemplates"],
    }),
    editResourceTemplate: builder.mutation({
      query: (params) => {
        return {
          url: `resourceAssessmentTemplates/${params.id}`,
          method: "PUT",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["ResourceTemplates"],
    }),
    deleteResourceTemplate: builder.mutation({
      query: (params) => {
        return {
          url: `resourceAssessmentTemplates/${params.id}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["ResourceTemplates"],
    }),

    // organizations
    getOrganizations: builder.query({
      query: (id) => {
        return {
          url: "orgs/",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Organizations"],
    }),
    newOrganization: builder.mutation({
      query: (params) => {
        return {
          url: "orgs/",
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        };
      },
      invalidatesTags: ["Organizations"],
    }),
    editOrganization: builder.mutation({
      query: (params) => {
        return {
          url: `orgs/${params.id}`,
          method: "PUT",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Organizations"],
    }),
    deleteOrganization: builder.mutation({
      query: (params) => {
        return {
          url: `orgs/${params.id}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Organizations"],
    }),

    // designation
    getAllDesignations: builder.query({
      query: (id) => {
        return {
          url: `designations/?page=${id.page}&limit=${id.limit}&search=${id.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Designations"],
    }),
    newDesignation: builder.mutation({
      query: (params) => {
        return {
          url: "designations/",
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        };
      },
      invalidatesTags: ["Designations"],
    }),
    editDesignation: builder.mutation({
      query: (params) => {
        return {
          url: `designations/${params.id}`,
          method: "PUT",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Designations"],
    }),
    deleteDesignation: builder.mutation({
      query: (params) => {
        return {
          url: `designations/${params.id}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Designations"],
    }),
    uploadDesignation: builder.mutation({
      query: (params) => {
        return {
          url: `designations/upload`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["Designations"],
    }),

    // personnel assessments
    deletePersonnelAssessment: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.locationId}/personnelAssessments/${params.personnelAssessmentId}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["PersonnelAssessments"],
    }),
    modifyPersonnelAssessment: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.locationId}/personnelAssessments/${params.personnelAssessmentId}`,
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["PersonnelAssessments"],
    }),
    getPersonnelAssessmentsOfLocation: builder.query({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.locationId}/personnelAssessments`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["PersonnelAssessments"],
    }),
    getPersonnelAssessmentsOfTimeslot: builder.query({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/personnelAssessments`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["PersonnelAssessments"],
    }),
    newPersonnelAssessment: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.locationId}/personnelAssessments`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["PersonnelAssessments"],
    }),

    allocatedResourceDetails: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/resourceAllocationDetails`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Resources"],
    }),
    replaceResourceAllocations: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/replaceResourceAllocations`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Resources"],
    }),
    autoResourceAllocations: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/autoResourceAllocation?timeslotId=${params.timeslotId}`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: {},
        };
      },
      invalidatesTags: ["Resources"],
    }),

    // locations
    newLocation: builder.mutation({
      query: (params) => {
        console.log("newLocation, params:", params);
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Locations"],
    }),
    editLocation: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.id}`,
          method: "PUT",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Locations"],
    }),
    deleteLocation: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.id}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Locations"],
    }),
    getAllLocations: builder.query({
      query: (params) => {
        console.log("getAllLocations, params:", params);
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Locations"],
    }),
    uploadLocations: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/upload`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["Locations"],
    }),
    swapSerialNos: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/swapSerialNos`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["Locations"],
    }),
    getLocationDetails: builder.query({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.locationId}/details`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Locations"],
    }),

    // personnel
    getAllPersonnel: builder.query({
      query: (id) => {
        return {
          url: `personnel/?page=${id.page}&limit=${id.limit}&search=${id.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Personnel"],
    }),

    // TODO: not currently used.
    getAvailablePersonnel: builder.query({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/getAvailablePersonnel`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Personnel"],
    }),
    newPersonnel: builder.mutation({
      query: (params) => {
        return {
          url: "personnel/",
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params,
        };
      },
      invalidatesTags: ["Personnel"],
    }),
    deletePersonnel: builder.mutation({
      query: (params) => {
        return {
          url: `personnel/${params.personnelId}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Personnel"],
    }),
    modifyPersonnel: builder.mutation({
      query: (params) => {
        return {
          url: `personnel/${params.personnelId}`,
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Personnel"],
    }),
    uploadPersonnel: builder.mutation({
      query: (params) => {
        return {
          url: `personnel/upload`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["Personnel"],
    }),
    uploadPhoto: builder.mutation({
      query: (params) => {
        return {
          url: `personnel/uploadPhoto/${params.personnelId}`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["Personnel"],
    }),

    // external forces
    getExternalForces: builder.query({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/personnel/?page=${params.page}&limit=${params.limit}&search=${params.search}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["ExternalForces"],
    }),

    newExternalForce: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/personnel/`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.body,
        };
      },
      invalidatesTags: ["ExternalForces"],
    }),

    deleteExternalForce: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/personnel/${params.personnelId}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["ExternalForces"],
    }),

    modifyExternalForce: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/personnel/${params.personnelId}`,
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["ExternalForces"],
    }),

    uploadExternalForces: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/personnel/upload`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["ExternalForces"],
    }),

    uploadPhotoExternalForce: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/personnel/uploadPhoto/${params.personnelId}`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: params.formData,
        };
      },
      invalidatesTags: ["ExternalForces"],
    }),

    // personnel allocations

    // currently unused
    updatePersonnelAllocations: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/personnelAllocations/${params.locationId}`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["PersonnelAllocations"],
    }),
    autoPersonnelAllocations: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/autoPersonnelAllocation?timeslotId=${params.timeslotId}`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: {},
        };
      },
      invalidatesTags: ["PersonnelAllocations"],
    }),

    // templates
    getAllTemplates: builder.query({
      query: (id) => {
        return {
          url: "personnelAssessmentTemplates/",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["PersonnelAssessmentTemplates"],
    }),
    newTemplate: builder.mutation({
      query: (body) => {
        return {
          url: "personnelAssessmentTemplates/",
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        };
      },
      invalidatesTags: ["PersonnelAssessmentTemplates"],
    }),
    editTemplate: builder.mutation({
      query: (params) => {
        return {
          url: `personnelAssessmentTemplates/${params.id}`,
          method: "PUT",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["PersonnelAssessmentTemplates"],
    }),
    deleteTemplate: builder.mutation({
      query: (params) => {
        return {
          url: `personnelAssessmentTemplates/${params.id}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["PersonnelAssessmentTemplates"],
    }),

    // currently unused?
    setLocations: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.id}/setLocations`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Locations"],
    }),

    allocatedPersonnelDetails: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/personnelAllocationDetails`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["PersonnelAllocations", "Bandobast"],
    }),

    replacePersonnelAllocations: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/replacePersonnelAllocations`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["PersonnelAllocations", "Bandobast"],
    }),

    // timeslots
    getTimeslots: builder.query({
      query: (bandobastId) => {
        return {
          url: `bandobast/${bandobastId}/timeslots/`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: [
        "Timeslots",
        "Locations",
        "PersonnelAllocations",
        "PersonnelAssessments",
      ],
    }),
    newTimeslot: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Timeslots"],
    }),
    modifyTimeslot: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}`,
          method: "PUT",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["Timeslots"],
    }),
    deleteTimeslot: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}`,
          method: "DELETE",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["Timeslots"],
    }),
    getTimeslotPersonnelDetails: builder.query({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/personnelDetails`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: [
        "Timeslots",
        "PersonnelAssessments",
        "PersonnelAllocations",
      ],
    }),

    getTimeslotResourceDetails: builder.query({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/resourceDetails`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Timeslots", "ResourceAssessments", "ResourceAllocations"],
    }),

    // schedules
    getSchedules: builder.query({
      query: (unused) => {
        return {
          url: "personnel/schedules",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Schedules"],
    }),

    // checkin status
    checkinStatus: builder.query({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/checkinStatus`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["Schedules"],
    }),

    getResAss: builder.query({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.locationId}/resourceAssessments`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      providesTags: ["RA"],
    }),

    newResAss: builder.mutation({
      query: (params) => {
        console.log("in newResourceAssessment, params: ", params);
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.locationId}/resourceAssessments`,
          method: "POST",
          headers: {
            authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["RA"],
    }),

    deleteResAss: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.locationId}/resourceAssessments/${params.resourceAssessmentId}`,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        };
      },
      invalidatesTags: ["RA"],
    }),

    modifyResAss: builder.mutation({
      query: (params) => {
        return {
          url: `bandobast/${params.bandobastId}/timeslots/${params.timeslotId}/locations/${params.locationId}/resourceAssessments/${params.resourceAssessmentId}`,
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params.body),
        };
      },
      invalidatesTags: ["RA"],
    }),
  }),
});

export const {
  // users
  useCreateUserMutation,
  useLoginUserMutation,
  useGetAllUsersQuery,
  useModifyUserMutation,
  useGetRolesByUserIdQuery,
  useRemoveRoleMutation,
  useAddRoleMutation,
  useChangePwdMutation,
  useResetPwdMutation,
  useDeleteUserMutation,

  // audit
  useGetAuditLogsQuery,
  useGetAuditLogsByIdQuery,

  // roles
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useAddPermissionToRoleMutation,
  useRemovePermissionFromRoleMutation,

  // permissions
  useGetAllPermissionsQuery,
  useGetPermissionsOfRoleQuery,
  useGetAvailablePermissionsQuery,

  // bandobast
  useGetAllBandobastQuery,
  useNewBandobastMutation,
  useDeleteBandobastMutation,
  useGetBandobastDetailsQuery,
  useGetBandobastSummaryQuery,
  useSetLocationsMutation,
  useEditBandobastMutation,
  useUploadLeftLogoMutation,
  useUploadRightLogoMutation,
  useUploadSignatureMutation,

  // personnel assessments (of locations)
  useNewPersonnelAssessmentMutation,
  useDeletePersonnelAssessmentMutation,
  useModifyPersonnelAssessmentMutation,
  useGetPersonnelAssessmentsOfTimeslotQuery,
  useGetPersonnelAssessmentsOfLocationQuery,

  // resource assessments (of locations)
  useAllocatedResourceDetailsMutation,
  useReplaceResourceAllocationsMutation,
  useAutoResourceAllocationsMutation,

  // personnel
  useGetAllPersonnelQuery,
  useNewPersonnelMutation,
  useGetAvailablePersonnelQuery,
  useDeletePersonnelMutation,
  useModifyPersonnelMutation,
  useUploadPhotoMutation,

  // external forces
  useGetExternalForcesQuery,
  useNewExternalForceMutation,
  useDeleteExternalForceMutation,
  useModifyExternalForceMutation,
  useUploadPhotoExternalForceMutation,
  useUploadExternalForcesMutation,

  // designations
  useGetAllDesignationsQuery,
  useNewDesignationMutation,
  useEditDesignationMutation,
  useDeleteDesignationMutation,
  useUploadDesignationMutation,

  // resource types
  useGetResourceTypesQuery,
  useNewResourceTypeMutation,
  useEditResourceTypeMutation,
  useDeleteResourceTypeMutation,
  useUploadResourceTypeMutation,

  // resources
  useGetResourcesQuery,
  useNewResourceMutation,
  useEditResourceMutation,
  useDeleteResourceMutation,
  useUploadResourceMutation,

  // resource templates
  useGetResourceTemplatesQuery,
  useNewResourceTemplateMutation,
  useEditResourceTemplateMutation,
  useDeleteResourceTemplateMutation,

  // personnel allocations
  useUpdatePersonnelAllocationsMutation,
  useAutoPersonnelAllocationsMutation,
  useReplacePersonnelAllocationsMutation,

  // locations
  // useGetLocationDetailsQuery,

  // templates
  useGetAllTemplatesQuery,
  useNewTemplateMutation,
  useEditTemplateMutation,
  useDeleteTemplateMutation,

  // attachments
  useUploadPersonnelMutation,

  // locations
  useGetAllLocationsQuery,
  useNewLocationMutation,
  useEditLocationMutation,
  useDeleteLocationMutation,
  useAllocatedPersonnelDetailsMutation,
  useUploadLocationsMutation,
  useSwapSerialNosMutation,
  useGetLocationDetailsQuery,

  // timeslots
  useGetTimeslotsQuery,
  useNewTimeslotMutation,
  useModifyTimeslotMutation,
  useDeleteTimeslotMutation,
  useGetTimeslotPersonnelDetailsQuery,
  useGetTimeslotResourceDetailsQuery,

  // schedules
  useGetSchedulesQuery,

  // organizations
  useGetOrganizationsQuery,
  useNewOrganizationMutation,
  useEditOrganizationMutation,
  useDeleteOrganizationMutation,

  // checkin
  useCheckinStatusQuery,

  useGetResAssQuery,
  useNewResAssMutation,
  useModifyResAssMutation,
  useDeleteResAssMutation,
} = applicationApi;
